import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { PictureAsPdf, OpenInNew } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../contexts/ToastContext';
import { apiService } from '../../services/api';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

interface NomenclaturaDocLinkProps {
  /** Variante compacta para listas */
  compact?: boolean;
}

/**
 * Enlace para ver el PDF de nomenclatura de sensores.
 * Oculto si no hay documento subido.
 */
export const NomenclaturaDocLink: React.FC<NomenclaturaDocLinkProps> = ({ compact = false }) => {
  const { t } = useTranslation();
  const { showError } = useToast();
  const [hasDocument, setHasDocument] = useState(false);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const info = await apiService.getNomenclaturaPdfInfo();
      setHasDocument(info.has_document);
    } catch {
      setHasDocument(false);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPdf = async () => {
    try {
      setOpening(true);
      const blob = await apiService.streamNomenclaturaPdf();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Error al abrir el documento');
    } finally {
      setOpening(false);
    }
  };

  if (loading || !hasDocument) return null;

  if (compact) {
    return (
      <Tooltip title={t('ensayos.nomenclaturaDoc.view', 'Ver guía de nomenclatura de sensores')}>
        <Button
          size="small"
          startIcon={opening ? <CircularProgress size={14} /> : <PictureAsPdf />}
          onClick={handleOpenPdf}
          disabled={opening}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          {t('ensayos.nomenclaturaDoc.guide', 'Guía nomenclatura')}
        </Button>
      </Tooltip>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1.5,
        borderRadius: 2,
        border: '1px dashed',
        borderColor: 'divider',
        bgcolor: 'action.hover',
      }}
    >
      <PictureAsPdf color="primary" fontSize="small" />
      <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
        {t('ensayos.nomenclaturaDoc.hint', 'Consulta la nomenclatura recomendada para que la aplicación detecte correctamente los sensores')}
      </Typography>
      <Button
        size="small"
        variant="outlined"
        startIcon={opening ? <CircularProgress size={16} /> : <OpenInNew />}
        onClick={handleOpenPdf}
        disabled={opening}
      >
        {t('ensayos.nomenclaturaDoc.viewPdf', 'Ver guía PDF')}
      </Button>
    </Box>
  );
};
